import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import ProductClassListV2 from 'adminDashboard/benefitDetails/common/ProductClassListV2'
import { ProductClassModalStateProps } from 'adminDashboard/benefitDetails/design/groupHra/classDesign'
import { BenefitDesignStep } from 'adminDashboard/benefitDetails/design/ichra/DesignStepsMapping'

import {
  BenefitDetailsResponse,
  BenefitProductClassResponse,
  PlanPeriodDto,
  RenewalBenefitDesignWizardResponse,
  benefitPlanPeriods,
  deleteIchraProductClass,
  getBenefitDetails,
  getBenefitProductClasses,
  getRenewalPlanPeriod,
  renewalBenefitDesignWizard,
} from 'core'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import ConfirmModal from 'common/components/dataDisplay/ConfirmModal'

import { BenefitPlanPeriodDropDown, Button, Card, Col, Row, Skeleton } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryParamsV2, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import SummaryTitle from '../../SummaryTitle'

const IchraDesignBenefitSummaryV2 = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const [productClassModalState, setProductClassModalState] = useState<ProductClassModalStateProps>({
    visible: false,
  })

  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()
  const [newClass, setNewClass] = useState<boolean>(false)
  const [deleteProductClass, setDeleteProductClass] = useState<BenefitProductClassResponse | null>(null)

  const { planPeriodIdQueryParam } = useQueryParamsV2()

  const { data: planPeriods } = useQueryRequestV2<PlanPeriodDto[]>({
    initialState: null,
    queryFn: () => benefitPlanPeriods(),
  })

  const {
    data: planPeriodDetails,
    isLoading: isPlanPeriodDetailsLoading,
    sendQuery: sendGetPlanPeriodDetails,
  } = useQueryRequestV2<BenefitDetailsResponse>({
    initialState: null,
    queryFn: () => getBenefitDetails(selectedPlanPeriod?.id),
    enable: !!selectedPlanPeriod,
  })

  const {
    data: productClasses,
    sendQuery: sendGetProductClasses,
    isLoading: isProductClassesLoading,
    reload: reloadProductClasses,
  } = useQueryRequestV2<BenefitProductClassResponse[]>({
    initialState: null,
    queryFn: () => getBenefitProductClasses(selectedPlanPeriod?.id),
    enable: !!selectedPlanPeriod,
  })

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const isRenewal = !!renewalPlanPeriod && !!planPeriodDetails && renewalPlanPeriod?.id === planPeriodDetails?.id

  const { data: productClassesInfo, reload: reloadProductClassesInfo } =
    useQueryRequestV2<RenewalBenefitDesignWizardResponse>({
      initialState: null,
      queryFn: () => renewalBenefitDesignWizard(),
      enable: isRenewal,
    })

  const { sendCommand: sendDeleteProductClass, isLoading: isDeleteProductClassLoading } = useCommandRequestV2(
    (productClassId: string) => deleteIchraProductClass(productClassId),
  )

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true)

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setSelectedPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  const handleBenefitStartDataEdit = () => {
    history.push(`${ROUTE_PATHS.DESIGN_BENEFIT_PATH}/${selectedPlanPeriod.id}/${BenefitDesignStep.BenefitStartDate}`)
  }

  const handleBenefitDesignEdit = (item: BenefitProductClassResponse) => {}

  const handleNewClass = () => {
    console.log('new class')
  }

  return (
    <Card
      title={
        <Row justify={'space-between'} className={'w-100'}>
          <Col>
            <SummaryTitle />
          </Col>
          <Col>
            {planPeriods && (
              <BenefitPlanPeriodDropDown
                defaultValue={planPeriodIdQueryParam}
                planPeriods={planPeriods}
                onPlanPeriodSelected={onPlanPeriodChanged}
              />
            )}
          </Col>
        </Row>
      }
      className="benefit_details__summary_v2"
    >
      <Skeleton active loading={isPlanPeriodDetailsLoading} paragraph={{ rows: 10 }}>
        {planPeriodDetails && (
          <>
            <Row>
              <Col sm={24} lg={17}>
                <Card className="summary_info_box_v2">
                  <Row>
                    <Col span={7}>
                      <span className="summary_info_title_v2">
                        {t('admin-dashboard.benefit-details.summary.start-date-title')}
                      </span>
                      <span className="summary_info_v2">{parseZoneFormat(planPeriodDetails.startDate)}</span>
                    </Col>
                    <Col span={7} className="left-bordered pl-8">
                      <span className="summary_info_title_v2">
                        {t('admin-dashboard.benefit-details.summary.notification-date')}
                      </span>
                      <span className="summary_info_v2">{parseZoneFormat(planPeriodDetails.notificationDate)}</span>
                    </Col>
                    <Col span={7} className="left-bordered pl-8">
                      <span className="summary_info_title_v2">
                        {t('admin-dashboard.benefit-details.summary.runout-period')}
                      </span>
                      <span className="summary_info_v2">{planPeriodDetails.runOutDays} days</span>
                    </Col>
                    <Col span={3}>
                      <Button
                        type="primary"
                        disabled={planPeriodDetails?.benefitDesignStatus === BenefitDesignStatus.Signed}
                        onClick={handleBenefitStartDataEdit}
                      >
                        <i className="fa fa-pencil-alt pr-8"></i>
                        {t('admin-dashboard.benefit-details.summary.edit-button')}
                      </Button>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Skeleton loading={isProductClassesLoading}>
              {productClasses && (
                <ProductClassListV2
                  productClasses={productClasses}
                  planPeriodId={planPeriodDetails.id}
                  isRenewal={isRenewal}
                  productClassesInfo={productClassesInfo}
                  onEdit={
                    planPeriodDetails.benefitDesignStatus !== BenefitDesignStatus.Signed
                      ? handleBenefitDesignEdit
                      : null
                  }
                  onRenewalEdit={handleBenefitDesignEdit}
                  onSubmit={async () => {
                    await reloadProductClasses()
                    await reloadProductClassesInfo()
                  }}
                  onDelete={
                    planPeriodDetails.benefitDesignStatus !== BenefitDesignStatus.Signed
                      ? (item) => setDeleteProductClass(item)
                      : null
                  }
                  onRenewalDelete={async (item) => {
                    await sendDeleteProductClass(item.id)
                    reloadProductClasses()
                    reloadProductClassesInfo()
                  }}
                  onNewClass={() => {
                    setNewClass(true)
                  }}
                />
              )}

              <ConfirmModal
                title="Create new ICHRA class"
                visible={newClass}
                buttonText="Confirm"
                children={
                  <div>
                    <p>You may add a new class to your current benefit plan period if it is not currently offered.</p>
                    <a
                      href="https://www.peoplekeep.com/blog/individual-coverage-hra-ichra-employee-classes"
                      target="_blank"
                    >
                      Learn more
                    </a>
                  </div>
                }
                onConfirm={() => {
                  console.log('confirm')
                  setNewClass(false)
                }}
                onCancel={() => {
                  setNewClass(false)
                }}
              ></ConfirmModal>
            </Skeleton>
          </>
        )}
      </Skeleton>
    </Card>
  )
}

export default IchraDesignBenefitSummaryV2
